import { useTrackingEventEmitter } from '@src/quince-site-common/src/helpers/tracking-event'
import { TrackingBrowserEventTypes } from '@src/quince-site-common/src/helpers/tracking-event/tracking-event'
import useDynamicStyle from '@src/quince-site-common/src/hooks/useDynamicStyle'
import classnames from 'classnames'
import React, { LegacyRef, ReactNode } from 'react'

import withTracking from '../../hocs/withTracking'
import { LinkWithOutTracking } from '../link/Link'
import { LoaderSVG } from '../loaderWrapper'

import { BUTTON_SIZE, BUTTON_TYPES } from './button.constants'
import styles from './button.module.scss'

export interface ButtonProps {
  id?: string
  children: ReactNode
  round?: boolean
  type?: BUTTON_TYPES
  size?: BUTTON_SIZE
  disabled?: boolean
  className?: string
  href?: string
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
  showLoader?: boolean
  selectorKey?: string
  classNames?: string[]
  mobileClassNames?: string[]
  dynamicValueClassesMobile?: string[]
  baseDynamicValueClasses?: string[]
  trackConfig?: any // TODO:  refactor this and add type for this
  trackingId?: string
  trackingTypes?: TrackingBrowserEventTypes[]
  buttonRef?: any
}

const Button: React.FC<ButtonProps> = ({
  id = '',
  children,
  type = BUTTON_TYPES.PRIMARY,
  className,
  onClick,
  disabled = false,
  round = true,
  href,
  size = BUTTON_SIZE.MEDIUM,
  showLoader = false,
  selectorKey,
  classNames,
  mobileClassNames,
  dynamicValueClassesMobile,
  baseDynamicValueClasses,
  trackConfig,
  trackingId,
  trackingTypes,
  buttonRef,
}: ButtonProps) => {
  const { className: dynamicClass, styleObject } = useDynamicStyle({
    baseClassNames: classNames,
    mobileClassNamesOverride: mobileClassNames,
    dynamicValueClassesMobileOverride: dynamicValueClassesMobile,
    baseDynamicValueClasses: baseDynamicValueClasses,
    // @i18n-ignore
    postFix: 'button',
  })
  const { elementRef } = useTrackingEventEmitter<HTMLButtonElement>(trackingId, trackingTypes)

  const classes = classnames(
    styles.btn,
    {
      [styles.btn__fromLink]: href,
      [styles.btn__noRadius]: !round,
      [styles.btn__disabled]: disabled,
    },
    styles[`btn__${size}`],
    styles[`btn__${type}`],
    className,
    'font-family--grosa',
    dynamicClass,
  )

  if (href) {
    return (
      <LinkWithOutTracking
        {...(id ? { id } : {})}
        className={classes}
        style={styleObject}
        href={href}
        onClick={onClick}
        disabled={disabled}
        withoutBaseStyle
        data-selector-key={selectorKey}
        trackConfig={trackConfig}
        ref={elementRef}
      >
        {children}
      </LinkWithOutTracking>
    )
  }
  return (
    <button
      {...(id ? { id } : {})}
      {...(id ? { ['data-testid']: id } : {})}
      className={classes}
      style={styleObject}
      onClick={onClick}
      disabled={disabled}
      data-selector-key={selectorKey}
      ref={(buttonRef || elementRef) as LegacyRef<HTMLButtonElement>}
    >
      {showLoader && <LoaderSVG className={styles.svgLoader} />}
      <span style={{ opacity: showLoader ? 0 : 1 }}>{children}</span>
    </button>
  )
}

export default withTracking(Button, true)
